import React, { useEffect, useState, forwardRef, useMemo } from 'react';
import styled from 'styled-components/macro';
import { borderRadiusPalette } from 'utils/styleConstants';
import Icon from './Icon';
import { useTheme } from 'providers/ThemeProvider';

const SearchBar = styled.input<{ small: boolean; bgColor?: string }>`
  height: ${(props) => (props.small ? '20px' : '30px')};
  line-height: ${(props) => (props.small ? '18px' : '30px')};
  width: inherit;
  border: none;
  outline-style: solid;
  outline-width: 1px;
  outline-color: ${({ small, theme }) =>
    small
      ? 'transparent'
      : theme.colorPalette.isDarkMode
      ? theme.colorPalette.grey.grey3
      : theme.colorPalette.grey.grey7};
  border-radius: ${(props) =>
    props.small ? borderRadiusPalette.searchInput.small : borderRadiusPalette.searchInput.normal};
  font-size: ${({ small, theme }) => (small ? theme.fontSizePalette.xxSmall : theme.fontSizePalette.xSmall)};
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey10 : theme.colorPalette.grey.grey7};
  background-color: ${({ theme, bgColor }) =>
    bgColor ? bgColor : theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.white};
  padding: 0 0 0 10px;
  font-family: ${({ theme }) => theme.fontFamily};
  padding-right: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  caret-color: ${({ theme }) => 
    theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark};
  &:focus {
    outline-width: 1px;
    outline-style: solid;
    outline-color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.light : theme.colorPalette.turquoise.dark};
  }

  &::placeholder {
    font-weight: light;
    color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey6 : theme.colorPalette.grey.grey7};
  }
`;
const SearchContainer = styled.div<{ fullWidth: boolean }>`
  position: relative;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '200px')};
  background-color: inherit;
`;


export interface SearchProps {
  value?: string;
  onChange?: Function;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onBlur?: Function;
  fullWidth?: boolean;
  placeholder?: string;
  small?: boolean;
  name?: string;
  id?: string;
  autocomplete?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  style?: React.CSSProperties;
  bgColor?: string;
  isSidebar?: boolean;
}

const SearchInput = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      id,
      value,
      placeholder = 'Search',
      onChange,
      small = false,
      name = '',
      fullWidth = false,
      onClick,
      autocomplete = true,
      onKeyDown,
      style,
      bgColor,
      isSidebar,
    },
    ref,
  ) => {
    const [currentValue, setCurrentValue] = useState(value || '');
    const { theme } = useTheme();
    useEffect(() => {
      setCurrentValue(value || '');
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(e.target.value);
      onChange && onChange(e);
    };

    const handleClear = () => {
      setCurrentValue('');
      onChange && onChange({ target: { value: '', name } } as any);
    };

    const [isFocused, setIsFocused] = useState(false);
    const iconColor = useMemo(() => {
      if (isSidebar && isFocused) {
        return theme.colorPalette.isDarkMode
          ? theme.colorPalette.turquoise.accent
          : theme.colorPalette.turquoise.dark;
      }

      return theme.colorPalette.isDarkMode
        ? theme.colorPalette.grey.grey9
        : theme.colorPalette.grey.grey7;
    }, [isSidebar, isFocused, theme]);

    return (
      <SearchContainer fullWidth={fullWidth}>
        <SearchBar
          ref={ref}
          id={id}
          key={`input-${id}`}
          name={name}
          small={small}
          placeholder={placeholder}
          style={style}
          type="text"
          value={currentValue}
          onChange={handleChange}
          onClick={onClick}
          autoComplete={autocomplete ? 'on' : 'off'}
          onKeyDown={onKeyDown}
          bgColor={bgColor}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <Icon
          icon={currentValue.length === 0 ? 'search' : 'times'}
          size={small ? 12 : 18}
          containerStyle={{
            position: 'absolute',
            top: small ? 4 : 5,
            right: 12,
            pointerEvents: currentValue.length === 0 ? 'none' : 'auto',
          }}
          style={{
            color: iconColor,
          }}
          onClick={currentValue.length === 0 ? undefined : handleClear}
        />
      </SearchContainer>
    );
  },
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
